import PhotoCaptureController from "./photo_capture_controller"

// Photo evidence + AI readout for one reading row in the scale modals —
// including rows that are not saved yet: for a persisted reading the
// attachment links directly; for a new row the attachment id is stashed in a
// pending_attachment_ids hidden input that the reading claims on save.
export default class extends PhotoCaptureController {
  static targets = ["input", "evidence", "status", "display"]
  static values = {
    uploadUrl: String,
    extractUrl: String,
    readingId: Number,
    fieldName: String,
    ai: Boolean
  }

  get thumbAlt() { return "Scale display photo" }
  get extractionFilename() { return "reading.jpg" }
  get savedMessage() { return "Photo saved as evidence." }
  get readingMessage() { return "Photo saved — reading the display…" }

  // A photo needs a reading row that can be saved: with no scale chosen the
  // usage would be kept for its evidence and then fail the whole workspace
  // save on the missing scale.
  beforeCapture() {
    if (this.scaleChosen()) return true
    this.setStatus("Choose the scale first, then photograph its display.", "warn")
    return false
  }

  attachmentUploaded(attachment) {
    if (!this.readingIdValue) this.addPendingInput(attachment.id)
  }

  attachmentRemoved(id) {
    this.element.querySelector(`input[type="hidden"][value="${CSS.escape(id)}"][name$="[pending_attachment_ids][]"]`)?.remove()
  }

  uploadFields(body) {
    body.append("weighing_attachment[category]", "scale_reading")
    body.append("weighing_attachment[caption]", this.caption())
    if (this.readingIdValue) body.append("weighing_attachment[scale_reading_id]", this.readingIdValue)
  }

  displayEdited() {
    if (this.filling) return
    this.displayTarget.classList.remove("ai-filled")
  }

  scaleChosen() {
    const select = this.element.closest(".scale-usage")?.querySelector("[data-reading-adjustment-target='scale']")
    return !select || Boolean(select.value)
  }

  caption() {
    const usage = this.element.closest(".scale-usage")
    const select = usage?.querySelector("[data-reading-adjustment-target='scale']")
    // Only a real selection carries a serial — the blank prompt option's
    // text ("Select calibrated scale…") must never become the caption.
    const serial = select?.value ? select.selectedOptions?.[0]?.text?.split(" · ")[0]?.trim() : null
    const wheel = usage?.querySelector("[data-wheel-field]")?.value
    return ["Scale display", [serial, wheel && `wheel ${wheel}`].filter(Boolean).join(" — ")]
      .filter(Boolean).join(" — ").trim() || "Scale display"
  }

  addPendingInput(id) {
    if (!this.fieldNameValue) return
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.fieldNameValue
    input.value = id
    this.element.appendChild(input)
  }

  applyExtraction(data) {
    const value = data.display_value
    const notes = data.notes ? ` (${data.notes})` : ""
    if (!data.found || value == null) {
      this.setStatus(`Photo saved — the display could not be read${notes}. Enter the value manually.`, "warn")
      return
    }
    if (data.unit === "lb" || data.unit === "other") {
      const unit = data.unit === "lb" ? `${value} lb` : `${value} in a non-kg unit${notes}`
      this.setStatus(`Photo saved — the display shows ${unit}. Convert and enter the kg value manually.`, "warn")
      return
    }

    const noUnit = data.unit === "unknown" ? " — no unit visible on the display" : ""
    const current = this.displayTarget.value.trim()
    if (current === "") {
      this.fillField(this.displayTarget, value)
      this.setStatus(`AI read ${value} kg (${data.confidence} confidence${noUnit}). Verify against the photo — the field stays editable.`, "ok")
    } else if (Number(current) === Number(value)) {
      this.setStatus(`AI read ${value} kg — matches the entered value.`, "ok")
    } else {
      this.setStatus(`AI read ${value} kg, which differs from the entered ${current}. Check the photo.`, "warn")
    }
  }
}
